/**
 * InlineTitleEdit — click-to-edit heading used by Beden Serileri and Ölçü
 * Grupları detail panels.
 *
 * The old "Düzenle" button opened a full dialog whose ONLY unique lever was
 * renaming the template itself; everything else (reorder / rename items /
 * add / delete) already had inline controls. Merchants told us the dialog
 * was a redundant round-trip. This component collapses that friction —
 * clicking the title turns it into an input, Enter saves, Escape cancels.
 *
 * Props:
 *   value        — current display name (string)
 *   onSave       — async (newName: string) → server-side rename call
 *   testId       — root data-testid
 *   className    — override wrapper classes (usually to preserve the h3 font)
 *   maxLength    — soft cap (default 60 chars)
 */
import { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";

export const InlineTitleEdit = ({ value, onSave, testId = "inline-title", className = "", maxLength = 60 }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef(null);

    // Keep local draft in sync when the parent value changes while we're
    // NOT editing (e.g. server refresh after a clone). Never overwrite an
    // in-progress edit.
    useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

    // Auto-focus + select when entering edit mode. Selecting the whole text
    // matches native input rename affordance (Finder / Nautilus etc).
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const commit = async () => {
        const next = draft.trim();
        if (!next) {
            toast.error("Ad boş olamaz");
            setDraft(value);
            setEditing(false);
            return;
        }
        if (next === value) { setEditing(false); return; }
        try {
            await onSave(next);
            // Toast fires from caller so it can carry accurate downstream copy
            // (e.g. "N tabloda güncellendi" — we don't know that count here).
        } catch (e) {
            // Revert on error. Caller usually toasts the message; only
            // surface a generic fallback if not.
            setDraft(value);
            if (!e?._silentToast) toast.error(e?.response?.data?.detail || "Ad güncellenemedi");
        } finally {
            setEditing(false);
        }
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={draft}
                maxLength={maxLength}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    else if (e.key === "Escape") { e.preventDefault(); setDraft(value); setEditing(false); }
                }}
                data-testid={`${testId}-input`}
                aria-label="Yeniden adlandır"
                className={`bg-transparent outline-none border-b-2 border-foreground/40 focus:border-foreground pb-0.5 min-w-0 w-full ${className}`}
            />
        );
    }

    return (
        <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={testId}
            className={`group inline-flex items-center gap-1.5 text-left cursor-text hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-foreground/30 rounded-sm ${className}`}
            title="Yeniden adlandır"
        >
            <span className="truncate">{value}</span>
            <Pencil
                className="h-3 w-3 shrink-0 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity"
                strokeWidth={2}
                aria-hidden="true"
            />
        </button>
    );
};

export default InlineTitleEdit;
